import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import { pollStatus, type StatusResult } from "@/features/identity-hub/api/identity-hub.api";
import { Sentry } from "@/lib/sentry";
import { getLocalUserFacingApiMessage, ApiError, passThroughMessage } from "@/lib/api-client";

/**
 * Shared hook for the Amber Path async pattern:
 * POST → get correlationId → poll `/status/:id` until terminal → invalidate + toast.
 *
 * @example
 * ```ts
 * const register = useAmberMutation({
 *   mutationFn: registerUser,
 *   pollBasePath: "/users",
 *   successMessage: "User registered",
 *   invalidateKeys: [identityHubKeys.all],
 * });
 * register.mutate({ email: "...", displayName: "..." });
 * ```
 */
export interface UseAmberMutationOptions<TInput> {
  /** Async function that POSTs and returns `{ correlationId }`. */
  mutationFn: (data: TInput) => Promise<{ correlationId: string }>;
  /** Base path for status polling (e.g. `/users`). */
  pollBasePath: string;
  /** Toast message on success — string, or a function receiving the mutation variables and the polled status result for dynamic messages (e.g. a count returned by `/status/:id`). Omit to skip the success toast entirely (e.g. when the caller drives its own UX). */
  successMessage?: string | ((variables: TInput, result: StatusResult) => string);
  /** Query keys to invalidate after success. */
  invalidateKeys?: readonly QueryKey[];
  /** Optional callback after successful mutation + poll. */
  onSuccess?: (result: StatusResult, variables: TInput) => void;
  /** Fires after the POST returns a correlation id, before status polling. */
  onAccepted?: (variables: TInput) => void;
  /** When true, skip the generic error toast (caller owns the message). */
  skipErrorToast?: (error: unknown) => boolean;
  /** Runs after invalidate, before the success toast — awaited so post-success side-effects (e.g. JWT refresh) finish before the user sees success. Errors are swallowed. */
  beforeToast?: (result: StatusResult, variables: TInput) => void | Promise<void>;
  /** Max poll attempts (default 20). */
  maxAttempts?: number;
  /** Initial poll interval in ms (default 500). */
  intervalMs?: number;
  /**
   * When true, a worker FAILED poll message may pass the leak gate in the error toast.
   * Customer-facing callers must leave this false (default).
   */
  passThroughWorkerFailure?: boolean;
}

/** Worker FAILED poll result with a non-empty human message — the only plain Error trusted for pass-through. */
export class AmberWorkerFailureError extends Error {
  override readonly name = "AmberWorkerFailureError";
}

export function amberFailureToastMessage(
  err: unknown,
  passThroughWorkerFailure = false,
): string | null {
  if (passThroughWorkerFailure && err instanceof AmberWorkerFailureError) {
    return getLocalUserFacingApiMessage(
      new ApiError(400, err.message),
      passThroughMessage,
    );
  }
  return getLocalUserFacingApiMessage(err);
}

export function useAmberMutation<TInput>(
  options: UseAmberMutationOptions<TInput>,
) {
  const {
    mutationFn,
    pollBasePath,
    successMessage,
    invalidateKeys,
    onSuccess,
    onAccepted,
    skipErrorToast,
    beforeToast,
    maxAttempts,
    intervalMs,
    passThroughWorkerFailure = false,
  } = options;

  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (data: TInput) => {
      const { correlationId } = await mutationFn(data);
      onAccepted?.(data);
      const result = await pollStatus(
        pollBasePath,
        correlationId,
        maxAttempts,
        intervalMs,
      );
      if (result.status === "FAILED") {
        const workerMessage = result.message?.trim();
        if (workerMessage) {
          throw new AmberWorkerFailureError(workerMessage);
        }
        throw new Error(result.error ?? "Operation failed");
      }
      return result;
    },
    onSuccess: async (result, variables) => {
      if (invalidateKeys) {
        await Promise.all(
          invalidateKeys.map((key) => qc.invalidateQueries({ queryKey: key })),
        );
      }
      if (beforeToast) {
        try { await beforeToast(result, variables); } catch { /* non-fatal — toast still fires */ }
      }
      if (successMessage) {
        toast.success(typeof successMessage === "function" ? successMessage(variables, result) : successMessage);
      }
      onSuccess?.(result, variables);
    },
    onError: (err) => {
      Sentry.captureException(err);
      if (skipErrorToast?.(err)) return;
      const message = amberFailureToastMessage(err, passThroughWorkerFailure);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}
